import React, { useState, useEffect, useCallback } from 'react'
import { Box } from '@mui/material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import JobStatusChangeDialog from '../PlannedJobs/components/JobStatusChangeDialog'
import ReportQuantityDialog from '../ReportQuantityDialog'
import CancelPartsDialog from '../CancelPartsDialog'
import AddPartsDialog from '../AddPartsDialog'
import AlertUI from '../../../../generic/AlertUI'
import DefaultBeforeSelectText from '../../common/DefaultBeforeSelectText'
import ActiveSingleJob from './ActiveSingleJob'
import { useJobData } from './hooks/useJobData'
import { apiGet, apiPost } from '../../../../generic/Api_Functions'
import useMosInfo from '../../common/provider/useMosInfo'
import useFrontEndConfig from '../../../../generic/hooks/useFrontEndConfig'
import { getJobAutoReportParts, getJobAutoReportResources, getJobBatchId, getJobExternalCircuit, getJobGovernedByAcceptance, getJobManagePreparation, getJobStatusCode, isJobInPreparation, hasPendingRawMaterialResources, resolveReportOutcome, shouldOpenInstanceSelectionModal } from '../../common/reportOutcome'
import { resolveJobModeActions } from '../../common/jobModeActions'
import useJobModeTransition from '../../common/useJobModeTransition'
import JobModeChangeDialog from '../../common/JobModeChangeDialog'
import BatchWarningDialog from '../../common/batch/BatchWarningDialog'
import BatchFinishDialog from '../../common/batch/BatchFinishDialog'
import { resolveBatchWarningKind, BATCH_WARNING_KIND } from '../../common/batch/batchWarning'
import { JOB_ACTION_INTENT } from '../PlannedJobs/utils/jobStatus'

// Historia 120 (Jupiter, paquete P10) -- S141/R-16: this widget (`ActiveSingleJobWidgetProvider`,
// "Active Single Job") is a FOURTH, independently selectable Finish surface -- alongside the Planned
// Jobs tabs and All Active Jobs (P6b) and the master-detail (P7/P9). Wired here with the SAME
// resolver/dialogs those already use (`common/batch/batchWarning.js`, `BatchWarningDialog`,
// `BatchFinishDialog`), never a copy of the condition. No new route, no new field: `getJobBatchId`
// reads the SAME per-job flag `common/reportOutcome.js` already exposes to the other three surfaces.
const CLOSED_BATCH_WARNING = { open: false, kind: BATCH_WARNING_KIND.NONE, batchId: '' }
const CLOSED_BATCH_FINISH = { open: false, batchId: '' }

const ActiveSingleJobContainer = () => {
  const { t_ } = useSmartTranslation()
  const {
    jobProgress,
    activeItems,
    fabricationOrder,
    jobReference,
    node,
    reloadProgress,
    materials,
    refreshActiveJobs
  } = useJobData()

  const { device, worker, unSelectNode, machineStorage } = useMosInfo()
  const [frontEndConfig] = useFrontEndConfig()

  const [changeStatusDialog, setChangeStatusDialog] = useState({ open: false })
  // S124: the two-action mode modal behind "Change status".
  const [modeChangeOpen, setModeChangeOpen] = useState(false)
  const [reportQuantityDialog, setReportQuantityDialog] = useState({ open: false, hideOkOption: false })
  const [cancelPartsDialog, setCancelPartsDialog] = useState({ open: false })
  const [addPartsDialog, setAddPartsDialog] = useState({ open: false })
  const [qualityTypes, setQualityTypes] = useState([])
  // S141 (P10): declared here, above every early return in this component (§7.2 #9 -- a hook below
  // one crashes the screen at runtime, real past incident on the sibling master-detail container).
  const [batchWarning, setBatchWarning] = useState(CLOSED_BATCH_WARNING)
  const [batchFinish, setBatchFinish] = useState(CLOSED_BATCH_FINISH)

  // Supervisor capability gates the cancel/add part actions. When no worker is logged in we still
  // show the actions so WorkerRequiredButton can prompt the presence flow (S31).
  const showPartActions = worker ? worker.supervisor === true : true
  const [counter, setCounter] = useState(0)
  const [AlertElement, showAlert] = AlertUI()

  // When INPUT_GREATER_AMOUNT_THAN_THE_OF is enabled (or not yet loaded), the first job may report
  // beyond planned to replace scrapped units. When disabled, every job is capped to "remaining".
  const allowGreaterThanFo = frontEndConfig?.inputGreaterAmountThanTheFo !== false
  const isFirstJob = jobProgress?.isFirst ?? false
  const maxReportable = isFirstJob && allowGreaterThanFo
    ? Infinity
    : isFirstJob
      ? Math.max(jobProgress?.remaining ?? 0, 0)
      : Math.min(jobProgress?.available ?? 0, jobProgress?.remaining ?? 0)

  const handleIncrement = () => setCounter((prev) => Math.min(prev + 1, maxReportable))
  const handleDecrement = () => setCounter((prev) => (prev > 0 ? prev - 1 : 0))
  const handleSetCounter = (value) => setCounter(Math.min(Math.max(value, 0), maxReportable))

  // Re-clamp the counter if the reportable limit decreases (progress refreshes every 5s)
  useEffect(() => {
    setCounter((prev) => Math.min(prev, maxReportable))
  }, [maxReportable])

  // Fetch quality types on mount
  useEffect(() => {
    let mounted = true
    apiGet('qualityTypesAll')
      .then(types => {
        if (mounted && Array.isArray(types)) {
          setQualityTypes(types)
        }
      })
      .catch(err => console.error('Error fetching quality types', err))
    return () => { mounted = false }
  }, [])

  const handleChangeStatus = (status) => {
    setChangeStatusDialog({ open: true, jobReference, fabricationOrder, statusCode: status, node })
  }

  // S141/R-16 (P10) -- THE derivation point for this widget's Finish button: the SAME resolver P6/P7
  // already use (`resolveBatchWarningKind`), fed the SAME per-job flag (`getJobBatchId`). A job with
  // no batch (absent/null/blank) resolves NONE and falls straight into today's individual close --
  // no behaviour change for the vast majority of jobs, which never belonged to a batch.
  const handleFinish = () => {
    const batchId = getJobBatchId(activeItems, fabricationOrder, jobReference)
    const kind = resolveBatchWarningKind({ batchId }, JOB_ACTION_INTENT.FINISH)
    if (kind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setBatchWarning({ open: true, kind, batchId })
      return
    }
    handleChangeStatus('FINISHED')
  }

  // S129/S134/S136-equivalent for this widget: dismissing the warning fires neither choice.
  const handleBatchWarningClose = () => setBatchWarning(CLOSED_BATCH_WARNING)

  // "Finish only this job" -- today's individual close, unchanged, no batch call of any kind.
  const handleBatchWarningChooseIndividual = () => {
    setBatchWarning(CLOSED_BATCH_WARNING)
    handleChangeStatus('FINISHED')
  }

  // "Finish the whole batch" -- N4+N5 via `BatchFinishDialog` (P8), mounted as-is, no new route.
  const handleBatchWarningChooseBatch = () => {
    const { batchId } = batchWarning
    setBatchWarning(CLOSED_BATCH_WARNING)
    setBatchFinish({ open: true, batchId })
  }

  const handleBatchFinishClose = () => setBatchFinish(CLOSED_BATCH_FINISH)

  const handleBatchFinished = () => {
    setBatchFinish(CLOSED_BATCH_FINISH)
    unSelectNode()
    refreshActiveJobs()
  }

  // S16/S37/S39/S19: posts the OK report and resolves the outcome. `instanceSelections` is only
  // populated when the SELECCION modal ran first (S16, added to the single report line — fleco 1,
  // "one modal per batch"); it is omitted otherwise so the request body is unchanged (CLAUDE.md:
  // no null/undefined placeholders in business logic).
  // Story 79 / S33: `generateMissingMaterial` travels the same way — the operator marked "create
  // required material" in that modal, and Sun derives the missing amount for the whole report.
  const submitOkReport = async (qty, instanceSelections = [], generateMissingMaterial = false) => {
    try {
      const okType = qualityTypes.find(t => t.code === 'Ok')

      if (okType && worker && device) {
        const quantityReports = [{
          comment: '',
          qualityType: okType.id,
          quantity: Number(qty),
          ...(instanceSelections.length > 0 ? { instanceSelections } : {}),
          ...(generateMissingMaterial ? { generateMissingMaterial: true } : {})
        }]
        const result = await apiPost('mos/fabricationOrderReportQuantity/' + worker.id + '/' + device.id + '/' + fabricationOrder.id + '/' + jobReference.id, quantityReports)
        reloadProgress()
        // S37/S39/S19: the endpoint now returns {solicitadas, reportadas, falloEn, motivo} instead
        // of a fixed message — resolveReportOutcome turns it into the alert to show and, on a
        // partial report, the pending quantity so a retry only resends what is missing.
        const outcome = resolveReportOutcome({
          result,
          requestedQuantity: Number(qty),
          autoReportResources: getJobAutoReportResources(activeItems, fabricationOrder, jobReference),
          hasPendingResources: hasPendingRawMaterialResources(materials, node),
          t_
        })
        showAlert({ severity: outcome.severity, message: outcome.message })
        setCounter(outcome.pending)
      } else {
        // Fallback if quality type not found or worker/device missing
        setReportQuantityDialog({
          open: true,
          node,
          jobReference,
          fabricationOrder,
          statusCode: 'IN_PROGRESS',
          initialQuantity: qty
        })
      }
    } catch (error) {
      console.error(error)
      // Surface the backend message verbatim (e.g. "Cannot report N part(s) as OK: only M have
      // their required raw material fully consumed at this node") instead of a generic text.
      showAlert({ severity: 'error', message: t_(error.message || 'Error reporting quantity') })
    }
  }

  const handleReportOk = async (qty) => {
    if (qty <= 0) {
      showAlert({ severity: 'warning', message: t_('Report quantity cannot be empty') })
      return
    }
    if (jobProgress && (!isFirstJob || !allowGreaterThanFo)) {
      if (qty > maxReportable) {
        showAlert({ severity: 'warning', message: t_('Report quantity cannot be greater than remaining') })
        return
      }
    }

    // S16 (GATED — pending follow-up): SELECCION instance selection on report needs a per-piece,
    // multi-reference contract with Sun that is not closed yet (see the story's
    // dilema-seleccion-consumo-por-pieza.md). Until then we neither open the incomplete modal nor
    // post an ambiguous report: we tell the operator to consume from the Components widget. The
    // detector stays so re-enabling is a one-line swap.
    const autoReportResources = getJobAutoReportResources(activeItems, fabricationOrder, jobReference)
    if (shouldOpenInstanceSelectionModal(machineStorage, autoReportResources)) {
      showAlert({ severity: 'warning', message: t_('In Selection mode, consume the material from the Components widget') })
      return
    }

    await submitOkReport(qty)
  }

  const handleReportNok = (qty) => {
    if (qty <= 0) {
      showAlert({ severity: 'warning', message: t_('Report quantity cannot be empty') })
      return
    }
    if (jobProgress && (!isFirstJob || !allowGreaterThanFo)) {
      if (qty > maxReportable) {
        showAlert({ severity: 'warning', message: t_('Report quantity cannot be greater than remaining') })
        return
      }
    }
    setReportQuantityDialog({
      open: true,
      node,
      jobReference,
      fabricationOrder,
      statusCode: 'IN_PROGRESS',
      initialQuantity: qty,
      hideOkOption: true
    })
  }

  const handleStatusChangeSuccess = useCallback(() => {
    unSelectNode()
    setTimeout(() => refreshActiveJobs(), 500)
  }, [unSelectNode, refreshActiveJobs])

  const handleReportQuantitySuccess = useCallback((outcome) => {
    setReportQuantityDialog(prev => ({ ...prev, open: false }))
    reloadProgress()
    if (outcome) {
      showAlert({ severity: outcome.severity, message: outcome.message })
      setCounter(outcome.pending)
    } else {
      setCounter(0)
    }
  }, [reloadProgress, showAlert])

  const handlePartActionSuccess = useCallback(() => {
    reloadProgress()
    refreshActiveJobs()
  }, [reloadProgress, refreshActiveJobs])

  const plannedEffective = jobProgress?.plannedEffective ?? 0
  const canFinish = jobProgress?.canFinish
  // When the job's technology auto-reports parts, clicking Finish makes Sun report the remaining
  // quantity automatically and close the job, so Finish must not be blocked at all — neither by the
  // pending quantity nor by canFinish (which stays false precisely because those parts are not yet
  // reported; requiring it here would deadlock the auto-report flow).
  const autoReportParts = getJobAutoReportParts(activeItems, fabricationOrder, jobReference)
  // S51 (CA11/RN24/D18, story 86): a job governed by the acceptance form is observable but not
  // operable from this widget — Pause and Finish both come out disabled, on top of (not instead of)
  // the existing gates above.
  const governedByAcceptance = getJobGovernedByAcceptance(activeItems, fabricationOrder, jobReference)
  // S79 (story 100, RN34/C9(1)): a JOB that crossed the external circuit gate is a DIFFERENT domain
  // concept, computed independently (own per-job lookup) — gates the SAME two controls as above,
  // PLUS Report (A8: six standard actions), which #86 never touched for the MOD job.
  const externalCircuitJob = getJobExternalCircuit(activeItems, fabricationOrder, jobReference)
  const pauseDisabled = governedByAcceptance || externalCircuitJob
  const finishDisabled = governedByAcceptance || externalCircuitJob || (!autoReportParts && ((jobProgress?.remaining ?? 0) > 0 || !canFinish))
  // Story 113 (S134, RN44): the second reason to gate the report. The seam already existed since
  // story 100, so this is one more term, not a new mechanism.
  const inPreparation = isJobInPreparation(activeItems, fabricationOrder, jobReference) &&
    getJobManagePreparation(activeItems, fabricationOrder, jobReference)
  const reportDisabled = externalCircuitJob || inPreparation
  // S62 (delta signed 2026-08-04): the explanation travels as its own value, NOT derived from the two flags
  // above — finishDisabled is also true for pending parts or a non-finishable job, and blaming those on the
  // acceptance form would mislead the operator. Empty string when it does not apply: MUI renders no tooltip.
  const externalCircuitJobTooltip = externalCircuitJob
    ? t_('External job: actions are governed by the external jobs widget')
    : ''
  const governedByAcceptanceTooltip = governedByAcceptance
    ? t_('Acceptance job: actions are governed by the acceptance form')
    : externalCircuitJobTooltip
  // S139/RN48: when a PRE-EXISTING gate also applies, the reason shown is ITS own -- preparation
  // never steals the explanation of a gate that was already blocking. The tooltip is resolved only
  // when the controls are actually disabled, so an execution-mode job governed by the acceptance
  // form keeps showing nothing on its enabled report controls, exactly as before the story.
  const reportDisabledTooltip = reportDisabled
    ? (governedByAcceptanceTooltip || t_('Reporting is only available in execution'))
    : ''
  // Story 113 (S120/S133, R-G): the button layout is NOT computed here -- it comes from the single
  // shared point the four station widgets consume. This widget only supplies what it already knew:
  // its own gates, plus C4/C5 from the active context.
  const managePreparation = getJobManagePreparation(activeItems, fabricationOrder, jobReference)
  const statusCode = getJobStatusCode(activeItems, fabricationOrder, jobReference)
  // Resolved BEFORE the layout: `busy` feeds the Go to execution button's disabled state.
  const { goToExecution, backToPreparation, busy: modeTransitionBusy } = useJobModeTransition({
    device,
    fabricationOrder,
    jobReference,
    worker,
    activeJobs: activeItems,
    refreshActiveJobs,
    showAlert,
    t_
  })
  const modeActions = resolveJobModeActions({
    managePreparation,
    statusCode,
    finishDisabled,
    pauseDisabled,
    gatedTooltip: governedByAcceptanceTooltip,
    modeTransitionBusy
  })
  const handleBackToPreparation = () => {
    setModeChangeOpen(false)
    backToPreparation()
  }
  const handlePauseFromModeDialog = () => {
    setModeChangeOpen(false)
    handleChangeStatus('INTERRUPTED')
  }
  // If we have a selected job, show the widget
  if (fabricationOrder && jobReference) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: (theme) => theme.zIndex.snackbar }}>
          {AlertElement}
        </Box>
        <JobStatusChangeDialog
          open={changeStatusDialog.open}
          onCancell={() => setChangeStatusDialog(prev => ({ ...prev, open: false }))}
          onSuccess={handleStatusChangeSuccess}
          node={changeStatusDialog.node}
          jobReference={changeStatusDialog.jobReference}
          fabricationOrder={changeStatusDialog.fabricationOrder}
          statusCode={changeStatusDialog.statusCode}
        />
        {/* S124-S126: exactly two actions behind "Change status" -- pause the work, or go back to
            preparation. Closing it sends nothing. */}
        <JobModeChangeDialog
          open={modeChangeOpen}
          onClose={() => setModeChangeOpen(false)}
          onBackToPreparation={handleBackToPreparation}
          onPauseWork={handlePauseFromModeDialog}
        />
        <ReportQuantityDialog
          open={reportQuantityDialog.open}
          onCancel={() => setReportQuantityDialog(prev => ({ ...prev, open: false }))}
          onSuccess={handleReportQuantitySuccess}
          jobReference={jobReference}
          fabricationOrder={fabricationOrder}
          statusCode={reportQuantityDialog.statusCode}
          quantity={reportQuantityDialog.initialQuantity}
          hideOkOption={reportQuantityDialog.hideOkOption}
          materials={materials}
        />
        <CancelPartsDialog
          open={cancelPartsDialog.open}
          onCancel={() => setCancelPartsDialog({ open: false })}
          onSuccess={handlePartActionSuccess}
          fabricationOrder={fabricationOrder}
          jobReference={jobReference}
          maxQuantity={jobProgress?.remaining ?? 0}
        />
        <AddPartsDialog
          open={addPartsDialog.open}
          onCancel={() => setAddPartsDialog({ open: false })}
          onSuccess={handlePartActionSuccess}
          fabricationOrder={fabricationOrder}
          jobReference={jobReference}
        />

        {/* S141 (P10): the ONE warning, reused verbatim -- this widget only ever produces
            FINISH_BATCH_MEMBER (it has no Start/group-shortcut/resume gesture of its own). */}
        <BatchWarningDialog
          open={batchWarning.open}
          kind={batchWarning.kind}
          onClose={handleBatchWarningClose}
          onChooseIndividual={handleBatchWarningChooseIndividual}
          onChooseBatch={handleBatchWarningChooseBatch}
        />

        {/* S141 (P10): "finish the whole batch" -- N4+N5 as-is, no new route (P8's dialog, unmodified). */}
        <BatchFinishDialog
          open={batchFinish.open}
          deviceId={device?.id}
          workerId={worker?.id}
          batchId={batchFinish.batchId}
          onClose={handleBatchFinishClose}
          onFinished={handleBatchFinished}
        />

        <ActiveSingleJob
          jobTitle={`${fabricationOrder.fabricationOrder} · ${jobReference.code || ''}`}
          jobDescription={`${fabricationOrder.partReference?.code || ''} · ${node?.name || ''}`}
          jobReference={jobReference}
          partReference={fabricationOrder?.partReference}
          node={node}
          progress={plannedEffective ? Math.round(jobProgress?.percentage ?? 0) : 0}
          target={plannedEffective || 0}
          completed={jobProgress?.done || 0}
          nok={jobProgress?.fail || 0}
          finishDisabled={finishDisabled}
          pauseDisabled={pauseDisabled}
          governedByAcceptanceTooltip={governedByAcceptanceTooltip}
          reportDisabled={reportDisabled}
          reportDisabledTooltip={reportDisabledTooltip}
          materials={materials}
          onPause={() => handleChangeStatus('INTERRUPTED')}
          onFinish={handleFinish}
          modeActions={modeActions}
          inPreparation={inPreparation}
          onGoToExecution={goToExecution}
          onChangeStatus={() => setModeChangeOpen(true)}
          modeChangeOpen={modeChangeOpen}
          onReportOk={handleReportOk}
          onReportNok={handleReportNok}
          counter={counter}
          setCounter={handleSetCounter}
          handleIncrement={handleIncrement}
          handleDecrement={handleDecrement}
          showPartActions={showPartActions}
          onCancelParts={() => setCancelPartsDialog({ open: true })}
          onAddParts={() => setAddPartsDialog({ open: true })}
        />
      </Box>
    )
  }

  if (activeItems.length === 0) {
    return <DefaultBeforeSelectText />
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <DefaultBeforeSelectText text={t_('Please select a job from the list')} />
    </Box>
  )
}

export default ActiveSingleJobContainer
